import * as Sentry from '@sentry/react';
import { CharacterPayload, CharacterPayloadSchema } from '@client/components';
import { NotificationManager, Utils as utils } from '@client/utility';

interface CreateCharacterResponse {
  char_uuid?: string;
  error?: string;
  is_moderated?: boolean;
  moderated_message?: string;
  issues?: string[];
}

const requiredCharacterFields: (keyof CharacterPayload)[] = [
  'title',
  'persona',
  'name',
  'model_instructions',
  'scenario',
  'description',
  'first_message',
  'tags',
  'gender',
  'creator',
];

const validateCharacterData = (character: CharacterPayload) => {
  const filteredCharacter = { ...character };
  delete filteredCharacter.profileImageFile;
  delete filteredCharacter.is_banned;
  delete filteredCharacter.is_owner;

  if (!filteredCharacter.attribute && filteredCharacter.persona) {
    filteredCharacter.attribute = filteredCharacter.persona;
  }

  const missingFields = requiredCharacterFields.filter((field) => {
    const value = filteredCharacter[field];
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  });

  if (missingFields.length > 0) {
    return { error: `Missing or undefined fields: ${missingFields.join(', ')}` };
  }

  try {
    const parsed = CharacterPayloadSchema.parse(filteredCharacter);
    if (!parsed.creator?.trim()) return { error: 'Creator is required.' };
    return { parsed };
  } catch (err: any) {
    return { error: err.message || 'Invalid character format.' };
  }
};

const notifySuccess = async (message: string, userName?: string, charName?: string) => {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') return;
  await NotificationManager.fire({
    title: 'Character Saved',
    body: message,
    userName,
    charName,
    vibrateIfPossible: true,
  });
};

const postCharacter = async (
  type: 'create' | 'update',
  filteredCharacter: CharacterPayload
): Promise<CreateCharacterResponse> => {
  const data = { type, character: filteredCharacter };
  const res = await utils.post<CreateCharacterResponse>('/api/CreateCharacter', data);

  if (res.error) throw new Error(res.error);
  if (res.is_moderated) throw new Error(res.moderated_message || '⚠️ Your character triggered moderation filters. Please revise and try again.');
  if (res.issues && res.issues.length > 0) throw new Error(`Character moderation issues: ${res.issues.join('; ')}`);

  return res;
};

const handleCharacterPost = async (
  type: 'create' | 'update',
  character: CharacterPayload,
  successMsg: string
): Promise<CreateCharacterResponse> => {
  const { parsed, error } = validateCharacterData(character);
  if (error) return { error };

  if (type === 'create' && !parsed!.char_uuid) {
    try {
      parsed!.char_uuid = crypto.randomUUID();
    } catch (err) {
      Sentry.captureException(err);
      return { error: 'Failed to generate character ID.' };
    }
  }

  if (type === 'update' && !parsed!.char_uuid) {
    return { error: 'Character UUID is required for updating.' };
  }

  try {
    if (Array.isArray(parsed!.gallery)) parsed!.gallery = parsed!.gallery.filter(Boolean);
    const response = await postCharacter(type, parsed!);
    await notifySuccess(successMsg, parsed!.creator, parsed!.name);
    return { char_uuid: parsed!.char_uuid, ...response };
  } catch (err: any) {
    return { error: err.message || 'Something went wrong.' };
  }
};

export const createCharacter = async (
  character: CharacterPayload,
  trackingFunctions?: {
    trackCharacterCreated?: (character: any, result?: { char_uuid?: string }) => void;
    trackCharacterCreationFailed?: (character: any, error: string) => void;
  }
): Promise<CreateCharacterResponse> => {
  try {
    const result = await handleCharacterPost('create', character, 'Character created successfully!');
    if (!result.error && trackingFunctions?.trackCharacterCreated) trackingFunctions.trackCharacterCreated(character, result);
    return result;
  } catch (error) {
    if (trackingFunctions?.trackCharacterCreationFailed) {
      trackingFunctions.trackCharacterCreationFailed(character, error instanceof Error ? error.message : 'Unknown error');
    }
    throw error;
  }
};

export const updateCharacter = async (
  character: CharacterPayload,
  trackingFunctions?: {
    trackCharacterUpdated?: (character: any, result?: { char_uuid?: string }) => void;
    trackCharacterUpdateFailed?: (character: any, error: string) => void;
  }
): Promise<CreateCharacterResponse> => {
  try {
    const result = await handleCharacterPost('update', character, 'Character updated successfully!');
    if (!result.error && trackingFunctions?.trackCharacterUpdated) trackingFunctions.trackCharacterUpdated(character, result);
    return result;
  } catch (error) {
    if (trackingFunctions?.trackCharacterUpdateFailed) {
      trackingFunctions.trackCharacterUpdateFailed(character, error instanceof Error ? error.message : 'Unknown error');
    }
    return { error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const createDraft = async (character: CharacterPayload, creatorUuid: string): Promise<{ error?: string }> => {
  const { parsed, error } = validateCharacterData(character);
  if (error) return { error };

  try {
    await utils.db.insert({ tables: 'draft_characters', data: { ...parsed!, creator_uuid: creatorUuid } });
    await notifySuccess('Draft saved successfully!', parsed!.creator, parsed!.name);
    return {};
  } catch (err: any) {
    Sentry.captureException(err);
    return { error: err.message || 'Failed to create draft.' };
  }
};
